/**
 * Shared Pricing Plans Data
 *
 * Single source of truth for all pricing-related data.
 * Used by both the landing page and dashboard upgrade modal.
 *
 * CRITICAL: Do not change planIDs - they map directly to Chargebee products.
 *
 * ## Monthly Pricing Model (First-Month Discount)
 *
 * Monthly plans use a two-tier pricing model:
 *
 * 1. Month 1: User subscribes to a discounted Chargebee plan ID (e.g. `plan-go-49-first` at $49).
 *    See `firstMonthMonthlyPrice` below and `getPlanId()` for plan ID construction.
 *
 * 2. Month 2+: An Inngest cron job (`changeFirstMonthSubscriptions` in
 *    `src/pages/api/inngest/change_first_month_susbcription.ts`) runs every 12h and swaps
 *    subscriptions renewing within 3 days to the regular plan ID (e.g. `plan-go-199` at $199).
 *    The swap uses Chargebee's `end_of_term` scheduling so the price change takes effect at renewal.
 *
 * The `monthlyRetailPrice` is the month-2+ renewal price shown as a strikethrough next to the
 * discounted first-month price, so users see the value they're getting.
 *
 * Yearly plans are a single flat rate with no first-month discount.
 */

import { normalizeAccountType } from "@/utils/account-type"

export interface PricingPlan {
    name: string
    price: {
        /** Month 2+ regular price for monthly billing. Also the base for monthlyRetailPrice. */
        Monthly: string
        /** Per-month price on yearly billing */
        Yearly: string
        /** Total yearly price billed upfront */
        YearlyTotal?: string
    }
    /** Discounted price shown for month 1 of monthly billing. If set, checkout uses `plan-{name}-{this}-first` plan ID. */
    firstMonthMonthlyPrice?: string
    /** Strikethrough price on monthly cards — set to the month-2+ renewal price (price.Monthly) so users see the real savings. */
    monthlyRetailPrice?: string
    planIDs: string[]
    description: string
    features: string[]
    featured?: boolean
    button?: {
        label: string
        href: string
    }
}

/**
 * Plan tier order for filtering (lowest to highest)
 */
const PLAN_ORDER = ["trial", "go", "grow", "glory"] as const
export type PlanTier = (typeof PLAN_ORDER)[number]

/**
 * Core pricing plans - CRITICAL: Do not change planIDs
 */
export const PRICING_PLANS: PricingPlan[] = [
    {
        name: "Go",
        price: { Monthly: "199", Yearly: "29", YearlyTotal: "348" },
        firstMonthMonthlyPrice: "49",
        monthlyRetailPrice: "199", // Month 2+ renewal price (shown as strikethrough)
        planIDs: [
            // New plan IDs
            "plan-go-49-first", "plan-go-199", "plan-go-348",
            // Legacy plan IDs (backward compat)
            "plan-49", "plan-350", "plan-first-month-69", "plan-1-day-trial-69", "plan-basic-99", "plan-basic-700", "plan-basic-149-first", "plan-basic-199", "plan-basic-1800", "plan-basic-69-first", "plan-go-69-first", "plan-go-1800"
        ],
        description: "Steady growth for new Instagram accounts.",
        button: {
            label: "Start growing free",
            href: "/setup"
        },
        features: ['<b class="follower-count">1,000 - 2,000</b> real followers/mo', "Growth starts within 24 hours", "100% real, engaged followers", "Live growth dashboard"]
    },
    {
        name: "Grow",
        featured: true,
        price: { Monthly: "299", Yearly: "41", YearlyTotal: "492" },
        firstMonthMonthlyPrice: "69",
        monthlyRetailPrice: "299", // Month 2+ renewal price (shown as strikethrough)
        planIDs: [
            // New plan IDs
            "plan-grow-69-first", "plan-grow-299", "plan-grow-492",
            // Legacy plan IDs (backward compat)
            "plan-99", "plan-699", "plan-first-month-99", "plan-1-day-trial-99", "plan-pro-149", "plan-pro-1180", "plan-pro-199-first", "plan-pro-299", "plan-pro-2150", "plan-pro-99-first", "plan-grow-99-first", "plan-grow-2150"
        ],
        description: "Unlimited growth & AI-powered targeting.",
        button: {
            label: "Start growing free",
            href: "/setup"
        },
        features: [
            '<b class="follower-count">2,500 - 3,500</b> real followers/mo',
            '<span class="outfame-max-gradient">Outfame MAX\u2122</span> \u2014 2.75x faster growth',
            "Competitor audience targeting",
            "Smart niche matching",
            "Auto profile optimizer",
            "<b>15</b> AI-generated posts/mo",
            "Real-time analytics"
        ]
    },
    {
        name: "Glory",
        price: { Monthly: "399", Yearly: "99", YearlyTotal: "1188" },
        firstMonthMonthlyPrice: "129",
        monthlyRetailPrice: "399", // Month 2+ renewal price (shown as strikethrough)
        planIDs: [
            // New plan IDs
            "plan-glory-129-first", "plan-glory-399", "plan-glory-1188",
            // Legacy plan IDs (backward compat)
            "plan-249", "plan-1799", "plan-first-month-199", "plan-1-day-trial-199", "plan-turbo-299", "plan-turbo-2140", "plan-turbo-299-first", "plan-turbo-399", "plan-turbo-3000", "plan-turbo-129-first", "plan-turbo-2040", "plan-glory-3000"
        ],
        description: "Maximum growth speed & fully managed.",
        button: {
            label: "Start growing free",
            href: "/setup"
        },
        features: [
            '<b class="follower-count">4,000+</b> guaranteed real followers/mo',
            '<span class="outfame-max-gradient">Outfame MAX\u2122</span> \u2014 2.75x faster growth',
            '<span class="boost-post-gradient">Boost Post</span> \u2014 Game the algorithm',
            "Dedicated account manager",
            "Viral content templates",
            "<b>30</b> AI-generated posts/mo",
            "Weekly growth reports sent to you",
            "Priority 24/7 support"
        ]
    }
]

/**
 * Filter plans for upgrade modal
 * Excludes current tier and all tiers below it
 *
 * Handles both legacy names (basic, pro, turbo) and new names (go, grow, glory)
 * by normalizing to new names before comparison.
 *
 * @param currentPlanName - The user's current plan name (e.g., "basic", "pro", "trial", "grow")
 * @returns Filtered array of plans available for upgrade
 *
 * @example
 * getFilteredPlans("trial")  // Returns [Go, Grow, Glory]
 * getFilteredPlans("go")     // Returns [Grow, Glory]
 * getFilteredPlans("basic")  // Returns [Grow, Glory] (basic = go)
 * getFilteredPlans("grow")   // Returns [Glory]
 * getFilteredPlans("pro")    // Returns [Glory] (pro = grow)
 * getFilteredPlans("glory")  // Returns [] (nothing higher)
 * getFilteredPlans("turbo")  // Returns [] (turbo = glory)
 */
export function getFilteredPlans(currentPlanName?: string): PricingPlan[] {
    if (!currentPlanName) return PRICING_PLANS

    // Normalize legacy names (basic→go, pro→grow, turbo→glory)
    const normalizedCurrent = normalizeAccountType(currentPlanName) as PlanTier
    const currentIndex = PLAN_ORDER.indexOf(normalizedCurrent)

    // If plan not found after normalization, show all plans
    if (currentIndex === -1) return PRICING_PLANS

    // Return only plans above current tier
    return PRICING_PLANS.filter((plan) => {
        const planIndex = PLAN_ORDER.indexOf(plan.name.toLowerCase() as PlanTier)
        return planIndex > currentIndex
    })
}

/**
 * Generate Chargebee plan ID for checkout
 *
 * @param plan - The pricing plan
 * @param period - Billing period (Monthly or Yearly)
 * @returns Chargebee plan ID string
 */
export function getPlanId(plan: PricingPlan, period: "Monthly" | "Yearly"): string {
    const planName = plan.name.toLowerCase()

    if (period === "Yearly") {
        // Yearly plans use the YearlyTotal price
        return `plan-${planName}-${plan.price.YearlyTotal}`
    }

    // Monthly uses first month pricing if available
    if (plan.firstMonthMonthlyPrice) {
        return `plan-${planName}-${plan.firstMonthMonthlyPrice}-first`
    }

    return `plan-${planName}-${plan.price.Monthly}`
}

/**
 * Apply discount percentage to a price
 *
 * @param price - Original price as number
 * @param discountPercent - Discount percentage (e.g., 20 for 20%)
 * @returns Discounted price, rounded to nearest whole number
 */
export function applyDiscount(price: number, discountPercent: number): number {
    if (!discountPercent || discountPercent <= 0) return price

    const discountedPrice = price * (1 - discountPercent / 100)
    const roundedPrice = Math.round(discountedPrice)

    // Make price more attractive (avoid round numbers)
    if (roundedPrice % 10 === 0 || discountedPrice % 1 === 0.5) {
        return roundedPrice - 1
    }

    return roundedPrice
}

/**
 * Get price for a plan based on period
 *
 * @param plan - The pricing plan
 * @param period - Billing period
 * @param useFirstMonth - Whether to use first month pricing (default: true for Monthly)
 * @returns Price as string
 */
export function getPlanPrice(plan: PricingPlan, period: "Monthly" | "Yearly", useFirstMonth = true): string {
    if (period === "Yearly") {
        return plan.price.Yearly
    }

    return useFirstMonth && plan.firstMonthMonthlyPrice ? plan.firstMonthMonthlyPrice : plan.price.Monthly
}

/**
 * Calculate yearly total from monthly price if not provided
 */
export function getYearlyTotal(plan: PricingPlan): string {
    return plan.price.YearlyTotal || String(parseInt(plan.price.Yearly) * 12)
}

/**
 * Calculate savings percentage between monthly and yearly
 */
export function calculateSavingsPercent(plan: PricingPlan): number {
    const monthly = parseInt(plan.price.Monthly)
    const yearly = parseInt(plan.price.Yearly)
    return Math.round(((monthly - yearly) / monthly) * 100)
}
